import React, { useMemo, useRef, useState } from 'react'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import { useTranslation } from 'react-i18next'
import { ReactComponent as CheckIcon } from 'assets/icons/check-copy-2.svg'
import styles from 'components/Order/OrderDetails/StatusChangeDialog/StatusChangeDialog.module.css'
import { PrimaryButton } from 'components/Shared/PrimaryButton'
import {
  momentToISOLocalDate,
  momentToISOLocalTime,
  momentToISOZonedDateTime,
  parseMomentFromLocalDateAndLocalTimeFields
} from 'utils/date'
import moment from 'moment-timezone'
import { Form, Formik } from 'formik'
import { FieldComponent } from 'components/Shared/FieldComponent'
import { DatePickerFormFieldInline } from 'components/Shared/DatePickerInline'
import {
  getDeliveryPointCargo,
  isLoaded,
  isUnloaded
} from 'components/Order/OrderDetails/DeliveryPointCargoApproveForm/deliveryPointCargoHelper'
import {
  ApproveCargoSchema,
  DeliveryPointCargoApproveForm
} from 'components/Order/OrderDetails/DeliveryPointCargoApproveForm'
import wrappingTypes from 'components/Order/OrderDetails/DeliveryPointCargoApproveForm/wrappingTypes.json'
import { OrderStatus } from 'services'
import { createUpdateStatusChangeSchema } from './schema'
import { ControlledSelectField } from '../../../Shared/ControlledSelectField'
import { OrderTimeZones } from '../../constants'

export function StatusChangeDialog ({ open, order, newStatus, statusChanges, onClose, minNewStatusDateTime }) {

  const { t } = useTranslation()
  const [animateIcon, setAnimateIcon] = useState(false)

  const isStatusLoaded = useMemo(() => isLoaded(newStatus), [newStatus])
  const isStatusUnloaded = useMemo(() => isUnloaded(newStatus), [newStatus])

  const action = useMemo(() => ({
    status: newStatus.status,
    deliveryPointPosition: newStatus.deliveryPointNumber
  }), [newStatus])

  const getBackwardStatusInitialDateTime = () => {
    const backwardStatusDateTime = statusChanges.find(statusChange => statusChange.status === newStatus.status &&
      (statusChange.deliveryPointPosition === newStatus.deliveryPointNumber || (statusChange.deliveryPointPosition === 0 && newStatus.deliveryPointNumber === null)))?.creationDateTime
    return {
      date: momentToISOLocalDate(moment(backwardStatusDateTime)),
      time: momentToISOLocalTime(moment(backwardStatusDateTime))
    }
  }

  const initialFormValues = () => {
    const dateTime = newStatus.backwardStatus ?
      getBackwardStatusInitialDateTime()
      : {
        date: momentToISOLocalDate(moment()),
        time: momentToISOLocalTime(moment())
      }
    let cargoBuilder = {}
    if (isStatusLoaded) {
      const expectedDeliveryPointCargo = getDeliveryPointCargo(order, action)
      cargoBuilder = {
        quantity: expectedDeliveryPointCargo.quantity,
        weight: expectedDeliveryPointCargo.weight,
        wrappingType: wrappingTypes.find(wt => wt.id === expectedDeliveryPointCargo.wrappingType)?.id || 'ELEMENT'
      }
    } else if (isStatusUnloaded) {
      const expectedDeliveryPointCargo = getDeliveryPointCargo(order, action)
      const actualDeliveryPointCargo = statusChanges.find(change =>
        change.status === OrderStatus.Loaded && change.deliveryPointPosition === action.deliveryPointPosition)?.deliveryPointCargo
      cargoBuilder = {
        quantity: actualDeliveryPointCargo?.quantity,
        weight: actualDeliveryPointCargo?.weight,
        wrappingType: wrappingTypes.find(wt => wt.id === actualDeliveryPointCargo?.wrappingType)?.id || 'ELEMENT',
        responsiblePerson: expectedDeliveryPointCargo.responsiblePerson,
        signed: expectedDeliveryPointCargo.signed
      }
    }
    return {
      ...dateTime,
      cargo: cargoBuilder,
      timeZone: moment.tz.guess(),
    }
  }

  const handleAnimation = () => {
    setAnimateIcon(!animateIcon)
  }
  const formRef = useRef()

  const handleClose = (result) => () => {
    if (formRef.current) {
      const minimumDateTime = moment(minNewStatusDateTime).local()
      const selectedDateTime = parseMomentFromLocalDateAndLocalTimeFields(formRef.current.values.date, formRef.current.values.time)
      const isSelectedDateTimeSameAsMinimumDateTime = selectedDateTime.isSame(minimumDateTime, 'minute')
      const newResult = {
        dateTime:  momentToISOZonedDateTime(
            isSelectedDateTimeSameAsMinimumDateTime ? minimumDateTime.add(1, 'second') : selectedDateTime),
        timeZone: formRef.current.values.timeZone,
        cancellationCause: formRef.current.values.cancellationCause,
        rejectionCause: formRef.current.values.rejectionCause,
        cargo: formRef.current.values.cargo,
        ...result
      }
      onClose(newResult)
    } else {
      onClose(result)
    }
    handleAnimation()
  }

  return (
    <div>
      <Dialog
        open={open}
        fullWidth
        maxWidth={'md'}
        onEntered={handleAnimation}
        onClose={handleClose(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <div className={styles.iconContainer}>
            <CheckIcon className={styles.checkIcon}/>
            <CheckIcon className={animateIcon
              ? styles.checkIconShadowTranslated
              : styles.checkIconShadow
            }/>
          </div>
          <DialogContentText className={styles.contentContainer}
                             id="alert-dialog-description">
            {t('order.orderStatusChange.content',
              {
                status: t(`order.orderStatusChange.status.${newStatus.status}`, {
                  deliveryPointNumber: newStatus.deliveryPointNumber,
                  intermediatePointNumber: newStatus.intermediatePointNumber
                }),
              })}
          </DialogContentText>
          <div className={'m-auto'}>
            <Formik
              innerRef={formRef}
              initialValues={initialFormValues()}
              enableReinitialize
              validationSchema={createUpdateStatusChangeSchema(formRef, minNewStatusDateTime).concat(ApproveCargoSchema)}
              onSubmit={values => values}>
              {
                () => (
                  <Form className={styles.formAlignedCenter}>
                    <div>
                      <DatePickerFormFieldInline
                        name={`date`}
                        label={'form.orderStatusChange.date'}
                        skipIcon
                        notRequiredTouchedToError/>
                      <FieldComponent
                        fieldName={`time`}
                        label={'form.orderStatusChange.time'}
                        skipErrorIcon
                        className={styles.timeField}
                        requireTouchToShowError={false}
                      />
                    </div>
                    <div className={styles.timezoneContainer}>
                      <ControlledSelectField
                        disableClear
                        options={OrderTimeZones}
                        fieldName={'timeZone'}
                        label={t('form.orderStatusChange.timeZone')}
                      />
                    </div>
                    {newStatus.status === OrderStatus.Cancelled && <FieldComponent fieldName={'cancellationCause'}
                                                                                   label={'form.orderStatusChange.cancellationCause'}
                                                                                   className={styles.causeField}
                                                                                   multiline/>
                    }
                    {newStatus.status === OrderStatus.Rejected && <FieldComponent fieldName={'rejectionCause'}
                                                                                  label={'form.orderStatusChange.rejectionCause'}
                                                                                  className={styles.causeField}
                                                                                  multiline/>
                    }
                    {(isStatusLoaded || isStatusUnloaded) &&
                      <DeliveryPointCargoApproveForm order={order} action={action}/>}
                  </Form>
                )
              }
            </Formik>
          </div>
        </DialogContent>
        <DialogActions
          className={styles.dialogActions}
          disableSpacing
        >
          <div className={'p-1'}>
            {newStatus.clientNotified && <PrimaryButton
              className={styles.dialogButton}
              onClick={handleClose({ confirmed: true, sendEmail: true })}
              label={'order.orderStatusChange.confirm'}
            />}
          </div>
          <div className={'p-1'}>
            <PrimaryButton
              onClick={handleClose({ confirmed: true, sendEmail: false })}
              label={'order.orderStatusChange.withoutEmail'}
            />
          </div>
          <div className={'p-1'}>
            <PrimaryButton
              onClick={handleClose({ confirmed: false })}
              label={'order.orderStatusChange.cancel'}
            />
          </div>
        </DialogActions>
      </Dialog>
    </div>
  )
}
